'use client';

import { c as _c } from "react/compiler-runtime";
import Link from 'next/link';
import { ArrowRight, Expand } from 'lucide-react';
import { useCustomQuery } from '@/lib/hooks/useCustomQuery';
import { initiativesApi } from '@/lib/api';
import type { ProviderOpportunity } from '@/lib/api/types';
import { useInitiativeDetailSheet, buildInitiativeNavigationList } from '@/lib/hooks/useInitiativeDetailSheet';
import { OBJECT_COMPONENT_CATEGORY_OPTIONS, OBJECT_COMPONENT_COMPENSATION_OPTIONS, formatEventDateRange } from '@sizzlenz/shared';
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from '@/components/ui/carousel';
function AsksCard(t0) {
  const $ = _c(34);
  const {
    opportunity,
    onExpand
  } = t0;
  const {
    initiative,
    component
  } = opportunity;
  let t1;
  if ($[0] !== component.category) {
    t1 = OBJECT_COMPONENT_CATEGORY_OPTIONS.find(o => o.value === component.category)?.label || component.category;
    $[0] = component.category;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const categoryLabel = t1;
  let t2;
  if ($[2] !== component.compensation) {
    t2 = OBJECT_COMPONENT_COMPENSATION_OPTIONS.find(o_0 => o_0.value === component.compensation)?.label || component.compensation;
    $[2] = component.compensation;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const compensationLabel = t2;
  let t3;
  if ($[4] !== initiative.endDate || $[5] !== initiative.startDate) {
    t3 = formatEventDateRange(initiative.startDate, initiative.endDate);
    $[4] = initiative.endDate;
    $[5] = initiative.startDate;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const dateStr = t3;
  const t4 = `/initiatives/${initiative.id}?source=asks`;
  let t5;
  if ($[7] !== onExpand) {
    t5 = e => {
      e.preventDefault();
      e.stopPropagation();
      onExpand();
    };
    $[7] = onExpand;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Expand className="h-3.5 w-3.5" />;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t5) {
    t7 = <button type="button" onClick={t5} className="absolute top-2 right-2 p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-gray-100 transition-colors cursor-pointer" title="Quick view">{t6}</button>;
    $[10] = t5;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== component.title) {
    t8 = <h3 className="font-semibold text-sm leading-snug line-clamp-3 pr-6">{component.title}</h3>;
    $[12] = component.title;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const t9 = initiative.owner?.name ? ` · ${initiative.owner.name}` : "";
  let t10;
  if ($[14] !== initiative.name || $[15] !== t9) {
    t10 = <p className="text-sm text-indigo-600 mt-1 leading-snug line-clamp-2">{initiative.name}{t9}</p>;
    $[14] = initiative.name;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== dateStr) {
    t11 = dateStr && <p className="text-xs text-muted-foreground mt-1">{dateStr}</p>;
    $[17] = dateStr;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== categoryLabel) {
    t12 = <span className="px-1.5 py-px rounded bg-blue-50 text-blue-700">{categoryLabel}</span>;
    $[19] = categoryLabel;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== compensationLabel || $[22] !== component.compensation) {
    t13 = component.compensation !== "flexible" && <span className="px-1.5 py-px rounded bg-green-50 text-green-700">{compensationLabel}</span>;
    $[21] = compensationLabel;
    $[22] = component.compensation;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== t12 || $[25] !== t13) {
    t14 = <div className="mt-auto pt-3 flex items-center gap-1.5 flex-wrap text-[10px]">{t12}{t13}</div>;
    $[24] = t12;
    $[25] = t13;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== t10 || $[28] !== t11 || $[29] !== t14 || $[30] !== t4 || $[31] !== t7 || $[32] !== t8) {
    t15 = <Link href={t4} className="relative flex flex-col h-full rounded-xl border bg-white p-4 hover:shadow-md transition-shadow cursor-pointer">{t7}{t8}{t10}{t11}{t14}</Link>;
    $[27] = t10;
    $[28] = t11;
    $[29] = t14;
    $[30] = t4;
    $[31] = t7;
    $[32] = t8;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  return t15;
}
export function ExploreAsksSection() {
  const $ = _c(26);
  const {
    showInitiativeDetail
  } = useInitiativeDetailSheet();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      queryKey: ["home-provider-opportunities"],
      queryFn: _temp,
      staleTime: 300000
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    data,
    isLoading
  } = useCustomQuery(t0);
  let t1;
  if ($[1] !== data?.opportunities) {
    t1 = data?.opportunities ?? [];
    $[1] = data?.opportunities;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const opportunities = t1;
  let t2;
  if ($[3] !== opportunities || $[4] !== showInitiativeDetail) {
    t2 = opportunity => {
      const navigationList = opportunities.length > 1 ? buildInitiativeNavigationList(opportunities.map(_temp2).filter(_temp3)) : undefined;
      showInitiativeDetail(opportunity.initiative, {
        navigationList
      });
    };
    $[3] = opportunities;
    $[4] = showInitiativeDetail;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const handleExpand = t2;
  if (isLoading) {
    let t3;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <h2 className="text-3xl font-bold text-gray-900 mb-8">Can you help with these Asks?</h2>;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    let t4;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <section className="py-16"><div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">{t3}<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">{Array.from({
              length: 3
            }).map(_temp4)}</div></div></section>;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    return t4;
  }
  if (!opportunities.length) {
    let t3;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <div className="flex items-center justify-between mb-8"><div><h2 className="text-3xl font-bold text-gray-900">Can you help with these Asks?</h2><p className="text-muted-foreground text-sm mt-1">Initiatives looking for venues, food, beverages and more.</p></div></div>;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    let t4;
    let t5;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <p className="text-lg font-semibold text-gray-800 mb-2">No open asks right now</p>;
      t5 = <p className="text-sm text-muted-foreground mb-5 max-w-md mx-auto">Need a venue, catering, products, or other support for your next initiative? Create an initiative and add your asks — providers will find you.</p>;
      $[9] = t4;
      $[10] = t5;
    } else {
      t4 = $[9];
      t5 = $[10];
    }
    let t6;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <section className="py-16"><div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">{t3}<div className="rounded-2xl border border-dashed border-gray-300 bg-white p-10 text-center">{t4}{t5}<Link href="/asks" className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white text-sm font-medium rounded-full hover:bg-primary-700 transition-colors cursor-pointer">Explore Asks<ArrowRight className="w-4 h-4" /></Link></div></div></section>;
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    return t6;
  }
  let t3;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div><h2 className="text-3xl font-bold text-gray-900">Can you help with these Asks?</h2><p className="text-muted-foreground text-sm mt-1">Initiatives looking for venues, food, beverages and more.</p></div>;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="flex items-center justify-between mb-8">{t3}<Link href="/asks" className="text-sm font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1 cursor-pointer">View all <ArrowRight className="w-4 h-4" /></Link></div>;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      align: "start",
      loop: false
    };
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== handleExpand || $[16] !== opportunities) {
    let t7;
    if ($[18] !== handleExpand) {
      t7 = opportunity_0 => <CarouselItem key={`${opportunity_0.initiative.id}-${opportunity_0.component.id}`} className="pl-4 basis-full sm:basis-1/2 lg:basis-1/3"><AsksCard opportunity={opportunity_0} onExpand={() => handleExpand(opportunity_0)} /></CarouselItem>;
      $[18] = handleExpand;
      $[19] = t7;
    } else {
      t7 = $[19];
    }
    t6 = opportunities.map(t7);
    $[15] = handleExpand;
    $[16] = opportunities;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[20] !== t6) {
    t7 = <CarouselContent className="-ml-4">{t6}</CarouselContent>;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  let t9;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <CarouselPrevious className="-left-4 lg:left-0" />;
    t9 = <CarouselNext className="-right-4 lg:right-0" />;
    $[22] = t8;
    $[23] = t9;
  } else {
    t8 = $[22];
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t7) {
    t10 = <section className="py-16"><div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">{t4}<Carousel opts={t5} className="w-full">{t7}{t8}{t9}</Carousel></div></section>;
    $[24] = t7;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  return t10;
}
function _temp4(_, i_0) {
  return <div key={i_0} className="h-[180px] rounded-xl bg-gray-200 animate-pulse" />;
}
function _temp3(init, idx, arr) {
  return arr.findIndex(i => i.id === init.id) === idx;
}
function _temp2(o) {
  return o.initiative;
}
function _temp() {
  return initiativesApi.getProviderOpportunities({
    componentStatus: "open",
    limit: 6
  });
}